import { Text } from '@/components/commons/typography/Text'
import { SeparatorDot } from '@/components/commons/ui/SeparatorDot'
import { LoadingText } from '@/components/skeleton/TextSkeleton'
import { cssClass, formatNumberWithSuffix } from '@/shared/utils'
import Link from 'next/link'
import { useHighlights } from 'containers/highlights/hooks/useHighlights'
import { useConnections } from 'containers/profile-network/hooks/useConnections'
import { useProfile } from 'containers/profile/hooks/useProfile'
import { useMemo } from 'react'
import { joinWithSlash } from 'utilities/links'
import { useProjects } from 'containers/projects/hooks/useProjects'
import { BlueCheckIcon } from '@/public/assets/v2/images/icons'
import { Tooltip } from '@mantine/core'
import { colorsV2 } from '@/styles/v2/vars/vars.colors'

interface ProfileStatsProps {
  version: string
}
export const ProfileStats = ({ version }: ProfileStatsProps) => {
  const { profile, isLoading } = useProfile()
  const { highlights } = useHighlights()
  const { data: projects } = useProjects()
  const { numberOfResults } = useConnections()

  const stats = useMemo(() => {
    const highlightsCount = highlights?.length || 0
    const projectsCount = projects?.length || 0

    return [
      {
        label: 'Highlights & Projects',
        value: highlightsCount + projectsCount,
        link: joinWithSlash(
          profile?.username?.toLocaleLowerCase() + '#highlights-overview'
        ),
      },
      {
        label: 'Audience Size',
        value: profile?.totalAudience || 0,
        verifiedValue: profile?.totalVerifiedAudience || 0,
        middle: true,
        link: joinWithSlash(
          profile?.username?.toLocaleLowerCase(),
          'analytics'
        ),
      },
      {
        label: 'Connections',
        value: numberOfResults || 0,
        link: joinWithSlash(
          profile?.username?.toLocaleLowerCase(),
          'network',
          'connections'
        ),
      },
    ]
  }, [
    highlights?.length,
    projects?.length,
    profile?.username,
    profile?.totalAudience,
    numberOfResults,
    profile?.totalVerifiedAudience,
  ])

  return (
    <div
      className={cssClass([
        'border-solid border-x-0 border-y-1',
        'border-secondary flex border-0--desktopXl',
        'justify-flex-start--desktopXl items-center--desktopXl gap-12--desktopXl',
        'border-b-0--tablet',
      ])}
    >
      {stats.map((stat, index) => (
        <div
          key={'stat_profile_' + version + (profile?.id || index) + stat.label}
          className={cssClass([
            stat?.middle && 'flex items-center gap-12',
            'flex-1 flex-none--desktopXl',
          ])}
        >
          {stat?.middle && (
            <div className="hidden block--desktopXl">
              <SeparatorDot />
            </div>
          )}

          <div
            className={cssClass([
              'flex items-center flex-column gap-2 flex-1',
              'py-4',
              'flex-row-reverse--desktopXl gap-8--desktopXl flex-none--desktopXl',
              stat?.middle &&
                'border-solid border-x-1 border-y-0 border-secondary border-0--desktopXl',
            ])}
          >
            {LoadingText(
              <Link href={stat.link}>
                <Text colorClass="text-info">
                  <div className="flex items-center gap-3">
                    {formatNumberWithSuffix(stat.value)}
                    {stat?.verifiedValue > 0 && (
                      <Tooltip
                        label={`${formatNumberWithSuffix(
                          stat?.verifiedValue
                        )} Verified Audience`}
                        px={12}
                        py={8}
                        fz={'12px'}
                        lh={'16px'}
                        ta={'center'}
                        color={colorsV2.background.dark.darkButtonFocused}
                        withArrow
                        arrowSize={8}
                      >
                        {/* Tooltip requires an element or component with a ref so we're using span */}
                        <span>
                          <BlueCheckIcon width="12px" height="12px" />
                        </span>
                      </Tooltip>
                    )}
                  </div>
                </Text>
              </Link>,
              isLoading,
              14,
              24
            )}

            <Text sizeClass="text-12 text-13--tablet" color="tertirary">
              <Link href={stat.link}>{stat.label}</Link>
            </Text>
          </div>

          {stat?.middle && (
            <div className="hidden block--desktopXl">
              <SeparatorDot />
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
